import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { LOGO_URL, isKidProfile, isKidBanner, mixByType, shuffle } from './tvAssets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MUSIC_VOL = 0.45;
const KID_MUSIC_VOL = MUSIC_VOL * 0.251; // -12 dB

export function TvScreensaver() {
  const { activeProfile } = useAuth();
  const kid = isKidProfile(activeProfile);
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/contents`, { params: { slim: 1 }, withCredentials: true })
      .then(({ data }) => {
        // Kid profile -> jeunesse only (incl. séries/docs jeunesse) ; adult -> the rest
        // Screensaver banners only (no trailers), fully random order
        const base = (data || []).filter(c => c.type !== 'game' && (kid ? isKidBanner(c) : !isKidBanner(c))
          && (c.tv_screensaver_url || c.banner_url || c.thumbnail_url));
        setItems(mixByType(shuffle(base)));
      })
      .catch(() => { /* ignore */ });
  }, [kid]);

  // Ambient music: loop, 2s fade-in on start, 2s fade-out on wake
  // Kid profile -> dedicated jeunesse track ; profile selection page / adult -> default track
  useEffect(() => {
    const a = new Audio(kid ? '/sounds/veille-jeunesse.mp3' : '/sounds/veille.mp3');
    const vol = kid ? KID_MUSIC_VOL : MUSIC_VOL;
    a.loop = true;
    a.volume = 0;
    a.play().catch(() => { /* ignore */ });
    const fadeIn = setInterval(() => {
      a.volume = Math.min(vol, a.volume + vol / 10);
      if (a.volume >= vol) clearInterval(fadeIn);
    }, 200);
    return () => {
      clearInterval(fadeIn);
      const fadeOut = setInterval(() => {
        a.volume = Math.max(0, a.volume - vol / 10);
        if (a.volume <= 0) { a.pause(); a.src = ''; clearInterval(fadeOut); }
      }, 200);
    };
  }, [kid]);

  // Loop: at the end of a cycle, reshuffle so the order is always random and
  // the next cycle never starts with the image that was just displayed
  const next = useCallback(() => {
    setIdx(i => {
      if (i + 1 < items.length) return i + 1;
      setItems(prev => {
        if (prev.length < 2) return prev;
        const s = shuffle(prev);
        if (s[0].id === prev[prev.length - 1].id) s.push(s.shift());
        return s;
      });
      return 0;
    });
  }, [items.length]);

  // Images advance every 8s
  useEffect(() => {
    if (!items.length) return;
    const t = setTimeout(next, 8000);
    return () => clearTimeout(t);
  }, [idx, items, next]);

  const cur = items[idx];

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 40, background: '#000', overflow: 'hidden' }} data-testid="tv-screensaver">
      {items.map((c, i) => (
        <div key={c.id} className={`tv-bg-layer ${i === idx ? 'tv-bg-active' : ''}`}>
          <img src={c.tv_screensaver_url || c.banner_url || c.thumbnail_url} alt="" />
        </div>
      ))}
      {/* Dark purple vignette above the slideshow, below the title/logo */}
      <div data-testid="tv-screensaver-vignette" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse at center, transparent 48%, rgba(21,8,38,0.55) 78%, rgba(14,5,26,0.92) 100%)',
        boxShadow: 'inset 0 0 9vw 3vw rgba(14,5,26,0.85)' }} />
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(0deg, rgba(0,0,0,0.75) 0%, transparent 30%)' }} />
      <img src={LOGO_URL} alt="Maya+" style={{ position: 'absolute', top: '2vw', right: '3vw', height: '2.2vw', opacity: 0.85 }} />
      {cur && (
        <div key={cur.id} className="tv-fade-in" style={{ position: 'absolute', left: '3.5vw', bottom: '2.5vw' }}>
          {cur.logo_url
            ? <img src={cur.logo_url} alt={cur.title} style={{ maxHeight: '12vw', maxWidth: '44vw', objectFit: 'contain' }} />
            : <p style={{ fontSize: '3.2vw', fontWeight: 800, textShadow: '0 0.15vw 0.9vw rgba(0,0,0,0.8)' }}>{cur.title}</p>}
        </div>
      )}
    </div>
  );
}
